<script setup>
  import { ref, computed, watch, onMounted } from "vue";
  import { storeToRefs } from "pinia";
  import { MousePointerClick, Presentation, ChevronsUpDown } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { EmptyState } from "@/components/episto-ui/empty-state";
  import { AiHtmlInput } from "@/components/episto-ui/ai";
  import { REPORT_VARIABLES } from "@/components/episto-ui/mini-rich-editor/variable-mention";
  import { useQuestionListStore } from "@stores/survey/reports/questionList";
  import { useSearchGroupListStore } from "@stores/survey/reports/searchGroupList";
  import { useTargetListStore } from "@stores/survey/reports/targetList";
  import { useChartTemplatesStore } from "@stores/chartTemplates/chartTemplates";
  import { useReportsV2Store } from "@stores/survey/reportsV2/reportsV2";
  import { useSurveyStore } from "@stores/survey/survey";
  import ChartFieldEditor from "./ChartFieldEditor.vue";
  import ImageFieldEditor from "./ImageFieldEditor.vue";
  import ReportSlidePreview from "./ReportSlidePreview.vue";
  import StreamingIndicator from "./StreamingIndicator.vue";

  const props = defineProps({
    slide: {
      type: Object,
      default: null,
    },
    template: {
      type: Object,
      default: null,
    },
  });

  const selectedFieldName = ref(null);
  const previewCollapsed = ref(false);
  const previewRef = ref(null);

  // When scaled to 0.5, the element still occupies its original height in the flow.
  // We apply a negative margin-bottom to reclaim the unused 50%.
  const previewReducedMargin = computed(() => {
    const el = previewRef.value?.$el;
    if (!el) return 0;
    return el.offsetHeight * 0.5;
  });

  // Store setup
  const questionListStore = useQuestionListStore();
  const { chartableCards, crosstabQuestionOptions } = storeToRefs(questionListStore);

  const searchGroupListStore = useSearchGroupListStore();
  const { groupOptions, populationOptions } = storeToRefs(searchGroupListStore);

  const targetListStore = useTargetListStore();
  const { targetOptions } = storeToRefs(targetListStore);

  const chartTemplatesStore = useChartTemplatesStore();
  const { chartTemplates } = storeToRefs(chartTemplatesStore);
  const { getCompatibleBaseTypes, isCrosstabCompatible } = chartTemplatesStore;

  const reportsStore = useReportsV2Store();
  const { currentReport } = storeToRefs(reportsStore);
  const { token: surveyToken } = storeToRefs(useSurveyStore());

  // dead since #1980: no producer sets slide._streaming anymore (reportsV2.chat.js
  // and the ghost slides it drove are gone), so this is always false.
  const isStreaming = computed(() => !!props.slide?._streaming);

  // Use the report theme background as the preview canvas (empty, no content shapes).
  // The structure overlays are rendered on top to show editable zones.
  const backgroundPreviewUrl = computed(() => {
    const skinType = props.template?.skin_type || 'content';
    const b64 = currentReport.value?.report_theme_backgrounds?.[skinType]?.preview_base64;
    return b64 ? `data:image/png;base64,${b64}` : null;
  });

  // Current bindings - centralized in the store (single source of truth)
  const currentBindings = computed(() => reportsStore.getEffectiveBindings(props.slide));

  // Get theme token directly from current report
  const themeToken = computed(() => currentReport.value?.master_report?.token);

  // Sort editable fields: text fields first (by Y position), then chart fields (by Y position)
  const editableFields = computed(() => {
    const fields = props.template?.editable_fields || [];
    const elements = props.template?.structure?.elements || [];

    const positionMap = {};
    elements.forEach((el) => {
      positionMap[el.name] = el.position?.y || 0;
    });

    const typeOrder = { text: 0, image: 1, chart: 2 };
    return [...fields].sort((a, b) => {
      const orderA = typeOrder[a.type] ?? 1;
      const orderB = typeOrder[b.type] ?? 1;
      if (orderA !== orderB) return orderA - orderB;
      const posA = positionMap[a.name] || 0;
      const posB = positionMap[b.name] || 0;
      return posA - posB;
    });
  });

  const selectedField = computed(() => {
    if (!selectedFieldName.value) return null;
    return editableFields.value.find((f) => f.name === selectedFieldName.value) || null;
  });

  // Reset selection when switching slides
  watch(
    () => props.slide?.id,
    () => {
      selectedFieldName.value = null;
    },
  );

  // Update bindings in the store
  function updateBindings(newBindings) {
    if (props.slide?.id) {
      reportsStore.updateSlideLocal(props.slide.id, newBindings);
    }
  }

  // Text field handlers
  function getBindingValue(fieldName) {
    return currentBindings.value[fieldName]?.value ?? "";
  }

  function setBindingValue(fieldName, value) {
    const newBindings = { ...currentBindings.value };
    newBindings[fieldName] = { type: "static", value };
    updateBindings(newBindings);
  }

  // Image field handler
  function setImageBinding(fieldName, image) {
    const newBindings = { ...currentBindings.value };
    newBindings[fieldName] = {
      type: "image",
      value: image.value ?? null,
      ...(image.photographer && {
        photographer: image.photographer,
        photographer_url: image.photographer_url,
      }),
    };
    updateBindings(newBindings);
  }

  // Chart field handler
  function updateFieldBinding(fieldName, newFieldBinding) {
    const newBindings = { ...currentBindings.value };
    newBindings[fieldName] = newFieldBinding;
    updateBindings(newBindings);
  }

  // Data fetching
  onMounted(async () => {
    // Fetch card list for chart source selection
    await questionListStore.fetchCardList();
    // Fetch questions for crosstab options
    await questionListStore.fetchQuestions();

    if (themeToken.value) {
      await chartTemplatesStore.fetchTemplates(themeToken.value, surveyToken.value);
      await chartTemplatesStore.fetchBaseTypes();
    }
    if (groupOptions.value.length === 0) {
      await searchGroupListStore.fetchSearchGroups();
    }
    if (targetOptions.value.length === 0) {
      await targetListStore.fetchTargets();
    }
  });

  watch(themeToken, (newToken, prevToken) => {
    if (newToken && newToken !== prevToken) {
      chartTemplatesStore.fetchTemplates(newToken, surveyToken.value);
      chartTemplatesStore.fetchBaseTypes();
    }
  });
</script>

<template>
  <div class="h-full flex flex-col min-h-0 overflow-hidden">
    <!-- No slide selected -->
    <div
      v-if="!slide"
      class="flex-1 flex items-center justify-center">
      <EmptyState
        :icon="Presentation"
        title="No slide selected" />
    </div>

    <!-- Slide selected -->
    <template v-else>
      <!-- Preview (collapsible) -->
      <div class="flex-shrink-0 p-4 pb-0">
        <div
          class="transition-all duration-200 origin-top"
          :style="previewCollapsed ? { transform: 'scale(0.5)', marginBottom: '-' + previewReducedMargin + 'px' } : {}">
          <ReportSlidePreview
            ref="previewRef"
            :structure="template?.structure"
            :bindings="currentBindings"
            :editable-fields="editableFields"
            :preview-image-url="backgroundPreviewUrl"
            :show-overlays="true"
            :interactive="!isStreaming"
            :selected-field-name="isStreaming ? null : selectedFieldName"
            :chart-templates="chartTemplates"
            @select-field="selectedFieldName = $event" />
        </div>
        <div class="flex justify-center mt-1">
          <Button
            variant="ghost"
            size="sm"
            class="h-6 text-xs text-muted-foreground gap-1"
            @click="previewCollapsed = !previewCollapsed">
            <ChevronsUpDown class="w-3 h-3" />
            {{ previewCollapsed ? 'Expand preview' : 'Collapse preview' }}
          </Button>
        </div>
        <!-- Streaming indicator -->
        <StreamingIndicator
          v-if="isStreaming"
          class="mt-2" />
      </div>

      <!-- Field editor (scrollable) — hidden during streaming -->
      <div
        v-if="!isStreaming"
        class="flex-1 min-h-0 overflow-y-auto p-4">
        <!-- No field selected: hint -->
        <EmptyState
          v-if="!selectedField"
          :icon="MousePointerClick"
          size="sm"
          class="py-8"
          title="Click on a zone to edit it" />

        <!-- Selected field editor -->
        <div
          v-else
          class="space-y-2">
          <!-- Text field -->
          <template v-if="selectedField.type === 'text'">
            <AiHtmlInput
              :model-value="getBindingValue(selectedField.name)"
              :mention-items="REPORT_VARIABLES"
              @update:model-value="setBindingValue(selectedField.name, $event)" />
          </template>

          <!-- Image field -->
          <template v-else-if="selectedField.type === 'image'">
            <ImageFieldEditor
              :binding="currentBindings[selectedField.name] || {}"
              @update:image="setImageBinding(selectedField.name, $event)" />
          </template>

          <!-- Chart field -->
          <template v-else-if="selectedField.type === 'chart'">
            <ChartFieldEditor
              :field-name="selectedField.name"
              :binding="currentBindings[selectedField.name] || {}"
              :chartable-cards="chartableCards"
              :chart-templates="chartTemplates"
              :target-options="targetOptions"
              :population-options="populationOptions"
              :crosstab-question-options="crosstabQuestionOptions"
              :group-options="groupOptions"
              :get-compatible-base-types="getCompatibleBaseTypes"
              :is-crosstab-compatible="isCrosstabCompatible"
              @update:binding="updateFieldBinding(selectedField.name, $event)" />
          </template>
        </div>
      </div>
    </template>
  </div>
</template>
